/**
 * Scene debug groups for floor/storey detection, toggled by the
 * FloorVisualizationWidget (by group name). All groups default to hidden.
 */

import * as THREE from 'three';
import type { DetectedFloorLevel, DetectedFloorPlane, FloorDetectionDebugData, SurfaceRole } from './types';

/** Viewer subset needed to manage debug scene groups. */
interface DebugViewer {
  scene: { scene: THREE.Scene };
  /** Overlay scene rendered ON TOP of the point cloud pass — the surface
   *  sample layers live here so they aren't buried under the cloud splats
   *  they were sampled from. */
  measurementScene: THREE.Scene;
}

export const FLOOR_DETECTION_SURFACES_GROUP = 'floor-detection-points-debug';
export const FLOOR_DETECTION_REJECTED_GROUP = 'floor-detection-disqualified-points-debug';
export const FLOOR_DETECTION_PATCHES_GROUP = 'floor-detection-patches-debug';
export const FLOOR_DETECTION_PLANES_GROUP = 'floor-detection-planes-debug';

function disposeObject3D(child: THREE.Object3D): void {
  if (child instanceof THREE.Mesh || child instanceof THREE.Line || child instanceof THREE.LineSegments || child instanceof THREE.Points) {
    child.geometry?.dispose();
    if (child.material instanceof THREE.Material) {
      child.material.dispose();
    }
  }
}

/** Remove (and dispose) an existing debug group by name, then add a fresh hidden one. */
function replaceGroup(scene: THREE.Scene, name: string): THREE.Group {
  const existing = scene.children.find((child) => child.name === name);
  if (existing) {
    scene.remove(existing);
    existing.traverse(disposeObject3D);
  }
  const group = new THREE.Group();
  group.name = name;
  group.visible = false; // Controlled by the debug widget.
  scene.add(group);
  return group;
}

const ROLE_COLORS: Record<SurfaceRole, THREE.Color> = {
  floor: new THREE.Color(0x00cc44),
  ceiling: new THREE.Color(0x3388ff),
  unused: new THREE.Color(0x999999),
  rejected: new THREE.Color(0xff8800),
};

/** One THREE.Points cloud with per-vertex colors for the given surface samples. */
function buildSurfacePoints(samples: FloorDetectionDebugData['surfaces']): THREE.Points {
  const positions = new Float32Array(samples.length * 3);
  const colors = new Float32Array(samples.length * 3);
  samples.forEach((s, i) => {
    positions[i * 3] = s.x;
    positions[i * 3 + 1] = s.y;
    positions[i * 3 + 2] = s.z;
    const c = ROLE_COLORS[s.role];
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const material = new THREE.PointsMaterial({ size: 6, sizeAttenuation: false, vertexColors: true, depthTest: false, transparent: true, opacity: 0.85 });
  return new THREE.Points(geometry, material);
}

/** Blue→red gradient used for storey coloring (bottom→top). */
const STOREY_COLORS = [new THREE.Color(0x0000ff), new THREE.Color(0x00ffff), new THREE.Color(0x00ff00), new THREE.Color(0xffff00), new THREE.Color(0xff0000)];

function storeyColor(index: number, count: number): THREE.Color {
  const t = count > 1 ? index / (count - 1) : 0;
  const scaled = t * (STOREY_COLORS.length - 1);
  const c1 = STOREY_COLORS[Math.floor(scaled)];
  const c2 = STOREY_COLORS[Math.ceil(scaled)];
  return new THREE.Color().lerpColors(c1, c2, scaled % 1);
}

/**
 * Rebuild all pipeline debug groups: column surfaces (colored by role),
 * rejected surfaces, room-patch boxes (colored by storey; magenta for
 * sub-levels), and the per-level floor+ceiling planes.
 */
export function buildFloorDetectionDebugGroups(viewer: DebugViewer, debug: FloorDetectionDebugData, levels: DetectedFloorLevel[]): void {
  // Surface sample layers render in the measurement overlay scene — in the
  // main scene they sit AT the sampled surfaces' depth and the point cloud
  // pass buries them under its own splats.
  const surfacesGroup = replaceGroup(viewer.measurementScene, FLOOR_DETECTION_SURFACES_GROUP);
  const usedSamples = debug.surfaces.filter((s) => s.role !== 'rejected');
  surfacesGroup.userData.debugCount = usedSamples.length;
  if (usedSamples.length > 0) surfacesGroup.add(buildSurfacePoints(usedSamples));

  const rejectedGroup = replaceGroup(viewer.measurementScene, FLOOR_DETECTION_REJECTED_GROUP);
  const rejectedSamples = debug.surfaces.filter((s) => s.role === 'rejected');
  rejectedGroup.userData.debugCount = rejectedSamples.length;
  if (rejectedSamples.length > 0) rejectedGroup.add(buildSurfacePoints(rejectedSamples));

  const patchesGroup = replaceGroup(viewer.scene.scene, FLOOR_DETECTION_PATCHES_GROUP);
  const storeyCount = levels.length;
  debug.patches.forEach((patch, i) => {
    const storeyIndex = debug.patchStoreyIndex[i] ?? -1;
    let color: THREE.Color;
    if (storeyIndex === -1) {
      color = new THREE.Color(0x444444);
    } else if (levels[storeyIndex]?.isSubLevel) {
      color = new THREE.Color(0xff00ff);
    } else {
      color = storeyColor(storeyIndex, storeyCount);
    }
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    patch.bounds.getSize(size);
    patch.bounds.getCenter(center);
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    const edges = new THREE.EdgesGeometry(geometry);
    geometry.dispose();
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color }));
    line.position.copy(center);
    patchesGroup.add(line);
  });

  visualizeDetectedLevels(viewer, levels);
}

/**
 * Per-level floor AND true-ceiling planes (ceiling at half opacity), sized to
 * the level's own footprint. Replaces the legacy floors-only planes group.
 */
export function visualizeDetectedLevels(viewer: DebugViewer, levels: DetectedFloorLevel[]): void {
  const group = replaceGroup(viewer.scene.scene, FLOOR_DETECTION_PLANES_GROUP);

  levels.forEach((level, index) => {
    const color = level.isSubLevel ? new THREE.Color(0xff00ff) : storeyColor(index, levels.length);
    const footprint = level.footprint ?? level.floor.bounds;
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    footprint.getSize(size);
    footprint.getCenter(center);

    const addPlane = (plane: DetectedFloorPlane, opacity: number): void => {
      const geometry = new THREE.PlaneGeometry(Math.max(size.x, 0.5), Math.max(size.y, 0.5));
      const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(center.x, center.y, plane.height);
      group.add(mesh);

      const edgesGeometry = new THREE.EdgesGeometry(geometry);
      const edgesMaterial = new THREE.LineBasicMaterial({ color });
      const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
      edges.position.copy(mesh.position);
      group.add(edges);
    };

    addPlane(level.floor, 0.3);
    if (level.ceiling) addPlane(level.ceiling, 0.15);
  });
}

/**
 * Legacy-shaped debug visualization: one semi-transparent plane per detected
 * floor plane. Still used by FloorSelectorTool for persisted floor groups.
 */
export function visualizeDetectedFloors(viewer: DebugViewer, floors: DetectedFloorPlane[], name: string = FLOOR_DETECTION_PLANES_GROUP): void {
  const group = replaceGroup(viewer.scene.scene, name);
  if (floors.length === 0) return;

  floors.forEach((floor, index) => {
    const color = storeyColor(index, floors.length);

    let size: THREE.Vector3;
    if (floor.bounds && typeof floor.bounds.getSize === 'function') {
      size = floor.bounds.getSize(new THREE.Vector3());
    } else if (floor.bounds && 'min' in floor.bounds && 'max' in floor.bounds) {
      // Persisted bounds may be plain objects rather than THREE.Box3.
      const min = floor.bounds.min;
      const max = floor.bounds.max;
      size = new THREE.Vector3(max.x - min.x, max.y - min.y, max.z - min.z);
    } else {
      size = new THREE.Vector3(10, 10, 0.2);
    }

    const planeGeometry = new THREE.PlaneGeometry(Math.max(size.x, 0.5), Math.max(size.y, 0.5));
    const planeMaterial = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3, side: THREE.DoubleSide });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.position.set(floor.center.x, floor.center.y, floor.height);
    plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), floor.normal);
    group.add(plane);

    const edgesGeometry = new THREE.EdgesGeometry(planeGeometry);
    const edgesMaterial = new THREE.LineBasicMaterial({ color, linewidth: 2 });
    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    edges.position.copy(plane.position);
    edges.quaternion.copy(plane.quaternion);
    group.add(edges);
  });
}
